import { showHttpError, showSuccess } from '@calendly/alert'
import { I18n } from '@calendly/i18n'
import { countrySupportStatusForNumber } from '@calendly/telephone-tools'
import { createAsyncThunk, createSlice, createAction } from '@reduxjs/toolkit'

import {
  list,
  setPrimary,
  generateVerificationCode,
  create,
  destroy
} from './api'

export const NOT_VERIFIED = 'initial'
export const VERIFICATION_AWAITING = 'awaitingVerification'
export const VERIFICATION_REQUESTING_CODE = 'verificationRequesting'
export const VERIFICATION_RESENDING_CODE = 'verificationResendingCode'

const addPhoneNumber = createAction('phoneNumbers/addPhoneNumber')

const removeByUuid = (collection, uuidToRemove) => collection.filter(({ uuid }) => uuid !== uuidToRemove)

const insertPhoneNumber = (collection, { uuid, e164_phone_number: e164PhoneNumber, country, primary }) => (
  [
    ...removeByUuid(collection, uuid),
    {
      uuid,
      e164_phone_number: e164PhoneNumber,
      country,
      primary
    }
  ]
)

export const removePhoneNumberConfirmation = createAction(
  'phoneNumbers/removePhoneNumberConfirmation'
)

export const fetchPhoneNumbers = createAsyncThunk(
  'phoneNumbers/fetchPhoneNumbers',
  async (userId, { dispatch, rejectWithValue }) => {
    const [success, response] = await list(userId)

    if (!success) {
      dispatch(showHttpError(response))

      return rejectWithValue(response)
    }

    const modifiedResponse = await Promise.all(response.map(async (object) => {
      const { countryCode } = await countrySupportStatusForNumber(object.e164_phone_number)
      return {
        ...object,
        countryCode
      }
    }))

    return modifiedResponse
  }
)

export const setPrimaryPhoneNumber = createAsyncThunk(
  'phoneNumbers/setPrimary',
  async ({ userId, phoneNumberUuid }, { dispatch, rejectWithValue }) => {
    const [success, response] = await setPrimary(userId, phoneNumberUuid)

    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue(response)
    }

    dispatch(showSuccess(I18n.t('publisher.flash.changes_saved')))
    return phoneNumberUuid
  }
)

const sendForVerification = createAsyncThunk(
  'phoneNumbers/sendForVerification',
  async ({ userId, phoneNumber }, { dispatch, rejectWithValue }) => {
    const [success, response] = await generateVerificationCode(userId, phoneNumber)

    if (success) return response

    if (response.type === 'conflict') {
      dispatch(addPhoneNumber(response.payload))
      return rejectWithValue({})
    }

    if (response.type === 'validation') {
      return rejectWithValue(response.payload.errors)
    }

    dispatch(showHttpError(response))
    return rejectWithValue({})
  }
)

export const sendVerificationCode = (userId, phoneNumber) => async (dispatch) => {
  dispatch(sendForVerification({ userId, phoneNumber, status: VERIFICATION_REQUESTING_CODE }))
}

export const resendVerificationCode = (userId, phoneNumber) => async (dispatch) => {
  dispatch(sendForVerification({ userId, phoneNumber, status: VERIFICATION_RESENDING_CODE }))
}

export const createPhoneNumber = createAsyncThunk(
  'phoneNumbers/createPhoneNumber',
  async ({ userId, phoneNumber, verificationCode }, { dispatch, rejectWithValue }) => {
    const [success, response] = await create(userId, phoneNumber, verificationCode)

    if (success) return response

    if (response.type === 'conflict') {
      return response.payload
    }

    if (response.type === 'validation') {
      return rejectWithValue(response.payload.errors)
    }

    dispatch(showHttpError(response))
    return rejectWithValue({})
  }
)

export const destroyPhoneNumber = createAsyncThunk(
  'phoneNumbers/destroy',
  async ({ userId, phoneNumberUuid }, { dispatch, rejectWithValue }) => {
    const [success, response] = await destroy(userId, phoneNumberUuid)

    if (success) {
      dispatch(showSuccess(I18n.t('publisher.flash.changes_saved')))

      dispatch(fetchPhoneNumbers(userId))
      return phoneNumberUuid
    }

    dispatch(showHttpError(response))
    return rejectWithValue({})
  }
)

const slice = createSlice({
  name: 'phoneNumbers',
  initialState: {
    loadingState: 'initial',
    phoneNumberRemovalStatus: 'ready',
    phoneNumberUuidToRemove: null,
    primaryPhoneNumberStatus: 'ready',
    collection: [],
    verification: {
      errors: {},
      status: NOT_VERIFIED
    }
  },
  reducers: {
    clearPhoneNumberErrors: (state) => {
      state.verification.errors = {}
    },
    notVerified: (state) => {
      state.verification.status = NOT_VERIFIED
      state.verification.errors = {}
    },
    removePhoneNumberCancel: (state) => {
      state.phoneNumberRemovalStatus = 'ready'
      state.phoneNumberUuidToRemove = null
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPhoneNumbers.pending, (state) => {
      state.loadingState = 'loading'
    })
    builder.addCase(fetchPhoneNumbers.fulfilled, (state, { payload: phoneNumbers }) => {
      state.collection = phoneNumbers
      state.loadingState = 'loaded'
    })
    builder.addCase(fetchPhoneNumbers.rejected, (state) => {
      state.loadingState = 'error'
    })
    builder.addCase(setPrimaryPhoneNumber.pending, (state) => {
      state.primaryPhoneNumberStatus = 'requesting'
    })
    builder.addCase(setPrimaryPhoneNumber.fulfilled, (state, { payload: phoneNumberUuid }) => {
      state.primaryPhoneNumberStatus = 'ready'

      state.collection = state.collection.map((phoneNumber) => ({
        ...phoneNumber,
        primary: phoneNumber.uuid === phoneNumberUuid
      }))
    })
    builder.addCase(setPrimaryPhoneNumber.rejected, (state) => {
      state.primaryPhoneNumberStatus = 'ready'
    })
    builder.addCase(sendForVerification.pending, (state, { meta: { arg: { status } } }) => {
      state.verification.status = status
      state.verification.errors = {}
    })
    builder.addCase(sendForVerification.fulfilled, (state) => {
      state.verification.status = VERIFICATION_AWAITING
      state.verification.errors = {}
    })
    builder.addCase(sendForVerification.rejected, (state, { payload: errors }) => {
      if (Object.keys(errors).length === 0) {
        return
      }

      state.verification.errors = errors
      state.verification.status = NOT_VERIFIED
    })
    builder.addCase(createPhoneNumber.fulfilled, (state, { payload: phoneNumber }) => {
      state.collection = insertPhoneNumber(state.collection, phoneNumber)
      state.verification.errors = {}
    })
    builder.addCase(createPhoneNumber.rejected, (state, { payload: errors }) => {
      state.verification.errors = errors
    })
    builder.addCase(addPhoneNumber, (state, { payload: phoneNumber }) => {
      state.collection = insertPhoneNumber(state.collection, phoneNumber)
      state.verification.errors = {}
    })
    builder.addCase(removePhoneNumberConfirmation, (state, { payload: phoneNumberUuidToRemove }) => {
      state.phoneNumberRemovalStatus = 'confirmation'
      state.phoneNumberUuidToRemove = phoneNumberUuidToRemove
    })
    builder.addCase(destroyPhoneNumber.pending, (state) => {
      state.phoneNumberRemovalStatus = 'requesting'
    })
    builder.addCase(destroyPhoneNumber.fulfilled, (state) => {
      state.phoneNumberRemovalStatus = 'ready'
      state.phoneNumberUuidToRemove = null
    })
    builder.addCase(destroyPhoneNumber.rejected, (state) => {
      state.phoneNumberRemovalStatus = 'ready'
      state.phoneNumberUuidToRemove = null
    })
  }
})

export const { actions: {
  clearPhoneNumberErrors,
  notVerified,
  removePhoneNumberCancel
},
reducer } = slice
